import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getTicketDetail,
  updateTicketStatus,
  updateTicketPriority,
  addTicketResponse,
  reopenSupportTicket,
  deleteSupportTicket,
  assignTicket
} from '../../services/api';
import { useAdminWebSocket } from '../../hooks/useAdminWebSocket';
import {
  FiArrowLeft,
  FiUser,
  FiMail,
  FiCalendar,
  FiClock,
  FiTag,
  FiEye,
  FiTrash2,
  FiRefreshCw,
  FiMessageSquare,
  FiPaperclip,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiCheckCircle,
  FiUserPlus,
  FiDownload,
  FiExternalLink,
  FiBarChart2,
  FiActivity,
  FiShield,
  FiLock,
  FiUnlock,
  FiCornerUpLeft,
  FiMoreVertical,
  FiEdit,
  FiCopy,
  FiShare,
  FiBell,
  FiStar,
  FiHelpCircle,
  FiSettings,
  FiGrid,
  FiList,
  FiChevronDown,
  FiChevronUp,
  FiArrowUpRight
} from 'react-icons/fi';

const TicketDetail = ({ admin }) => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [ticket, setTicket] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [dispute, setDispute] = useState(null);
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('conversation'); // conversation, info, attachments
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

    // ADD WebSocket state
    const [realTimeMessages, setRealTimeMessages] = useState([]);
    const { isConnected, subscribeToSupportTicket } = useAdminWebSocket();

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, realTimeMessages]);

  // ADD: WebSocket subscription for this ticket
  useEffect(() => {
    if (!ticketId || !isConnected) return;
    
    console.log(`✅ [AdminTicketDetail] Subscribing to support ticket ${ticketId}`);
    
    const unsubscribe = subscribeToSupportTicket(ticketId, (newMessage) => {
      console.log('🎯 [AdminTicketDetail] WebSocket message received:', newMessage);
      
      // Check if message is for this ticket and from user (not admin)
      if (newMessage.sender_type === 'user') {
        setRealTimeMessages(prev => {
          // Check if this replaces a temp message
          const tempIndex = prev.findIndex(msg => 
            msg.isTemp && 
            msg.sender_id === newMessage.sender_id &&
            msg.message === newMessage.message
          );
          
          if (tempIndex !== -1) {
            // Replace temp message
            const updated = [...prev];
            updated[tempIndex] = { ...newMessage, isTemp: false };
            return updated;
          }
          
          // Check if message already exists
          const exists = prev.some(msg => 
            msg.message_id === newMessage.message_id
          );
          
          if (exists) return prev;
          
          // Add new message
          return [...prev, newMessage];
        });
      }
      
      // Handle status updates
      if (newMessage.type === 'support_ticket_status') {
        // Update ticket status in real-time
        setTicket(prev => prev ? { ...prev, status: newMessage.status } : null);
      }
      
      // Handle assignment updates
      if (newMessage.type === 'ticket_assigned') {
        // Update assignment in real-time
        // You might need to fetch admin details
      }
    });
    
    return unsubscribe;
  }, [isConnected, ticketId, subscribeToSupportTicket]);

  // ADD: Combine regular messages with real-time messages
  const allMessages = React.useMemo(() => {
    if (!messages) return realTimeMessages;
    
    // Combine arrays
    const combined = [...messages, ...realTimeMessages];
    
    // Remove duplicates - prefer real-time messages (they have latest data)
    const seen = new Set();
    const uniqueMessages = [];
    
    // Process in reverse to keep newest duplicates
    for (let i = combined.length - 1; i >= 0; i--) {
      const msg = combined[i];
      const key = msg.message_id || msg.temp_id || JSON.stringify(msg);
      
      if (!seen.has(key)) {
        seen.add(key);
        uniqueMessages.unshift(msg);
      }
    }
    
    // Sort by date
    return uniqueMessages.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at) : new Date();
      const dateB = b.created_at ? new Date(b.created_at) : new Date();
      return dateA - dateB;
    });
  }, [messages, realTimeMessages]);

  
  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getTicketDetail(ticketId);
      
      if (response.success) {
        setTicket(response.data.ticket || response.data);
        setAttachments(response.data.attachments || []);
        setDispute(response.data.dispute || null);
        setOrder(response.data.order || null);
        setMessages(response.data.ticket?.messages || []);
        
        // Clear real-time messages when fetching fresh data
        setRealTimeMessages([]);
      } else {
        throw new Error(response.message || 'Failed to fetch ticket');
      }
    } catch (err) {
      setError('Failed to fetch ticket details: ' + err.message);
      setTimeout(() => navigate('/admin/support/tickets'), 2000);
    } finally {
      setLoading(false);
    }
  };


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAssignToMe = async () => {
    try {
      // Show immediate assignment
      if (ticket) {
        setTicket(prev => ({
          ...prev,
          assigned_to: admin.id,
          assigned_first_name: admin.first_name,
          assigned_last_name: admin.last_name
        }));
      }
      
      await assignTicket(ticketId, { assigned_to: admin.id });
      setSuccessMessage('Ticket assigned to you successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      fetchTicket();
    } catch (err) {
      setError('Failed to assign ticket: ' + err.message);
      // Revert on error
      if (ticket) {
        setTicket(prev => ({
          ...prev,
          assigned_to: null,
          assigned_first_name: null,
          assigned_last_name: null
        }));
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Show immediate status change
      if (ticket) {
        setTicket(prev => ({
          ...prev,
          status: newStatus
        }));
      }
      
      await updateTicketStatus(ticketId, { status: newStatus });
      setSuccessMessage(`Status updated to ${newStatus}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Fetch fresh data
      fetchTicket();
    } catch (err) {
      setError('Failed to update status: ' + err.message);
      // Revert if error
      if (ticket) {
        setTicket(prev => ({
          ...prev,
          status: prev.status // Restore previous status
        }));
      }
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      // Show immediate change
      if (ticket) {
        setTicket(prev => ({
          ...prev,
          priority: newPriority
        }));
      }
      
      await updateTicketPriority(ticketId, { priority: newPriority });
      setSuccessMessage(`Priority updated to ${newPriority}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      fetchTicket();
    } catch (err) {
      setError('Failed to update priority: ' + err.message);
      // Revert on error
      if (ticket) {
        setTicket(prev => ({
          ...prev,
          priority: prev.priority
        }));
      }
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) {
      setError('Message cannot be empty');
      return;
    }

    const tempId = Date.now() + Math.random();
  
    try {
      setSubmitting(true);
      setError('');
      
      
      const tempMessage = {
        temp_id: tempId,
        ticket_id: parseInt(ticketId),
        sender_id: admin.id,
        sender_type: 'admin',
        sender_name: `${admin.first_name} ${admin.last_name}`,
        message: reply.trim(),
        is_internal: isInternal,
        created_at: new Date().toISOString(),
        isTemp: true
      };
      
      // Show immediately
      setRealTimeMessages(prev => [...prev, tempMessage]);
      
      // Send to server
      const response = await addTicketResponse(ticketId, {
        message: reply,
        is_internal: isInternal
      });
      
      if (response.success) {
        // If we get a message_id from response, update the temp message
        if (response.message_id) {
          // Update temp message with real ID
          setRealTimeMessages(prev => 
            prev.map(msg => 
              msg.temp_id === tempId 
                ? { 
                    ...msg, 
                    message_id: response.message_id,
                    isTemp: false 
                  }
                : msg
            )
          );
        }
        
        setSuccessMessage('Response added successfully');
        setReply('');
        setIsInternal(false);
        
        // Fetch fresh data after 1 second to ensure consistency
        setTimeout(() => {
          fetchTicket();
        }, 1000);
        
      } else {
        throw new Error(response.error || 'Failed to send response');
      }
      
    } catch (err) {
      setError('Failed to send response: ' + err.message);
      // Remove temp message on error
      setRealTimeMessages(prev => 
        prev.filter(msg => msg.temp_id !== tempId)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopen = async () => {
    const reason = window.prompt('Please enter reason for reopening:');
    if (!reason) return;

    try {
      await reopenSupportTicket(ticketId, { reopen_reason: reason });
      setSuccessMessage('Ticket reopened successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchTicket();
    } catch (err) {
      setError('Failed to reopen ticket: ' + err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      setError('Please enter a reason for deletion');
      return;
    }

    try {
      await deleteSupportTicket(ticketId, { delete_reason: deleteReason });
      setSuccessMessage('Ticket deleted successfully');
      setTimeout(() => navigate('/admin/support/tickets'), 1500);
    } catch (err) {
      setError('Failed to delete ticket: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSenderType = (message, ticket) => {
    if (message.is_system === 1) return 'system';
    if (message.sender_id === 0) return 'system';
    if (message.sender_id === ticket.assigned_to) return 'admin';
    if (message.sender_id === ticket.user_id) return 'user';
    if (message.sender_id === ticket.seller_id) return 'seller';
    return 'user';
  };

  const getSenderName = (message, ticket) => {
    if (message.sender_name) return message.sender_name;
    
    if (getSenderType(message, ticket) === 'system') return 'System';
    if (getSenderType(message, ticket) === 'admin') return `${ticket.assigned_first_name} ${ticket.assigned_last_name}`;
    if (getSenderType(message, ticket) === 'user') return `${ticket.user_first_name} ${ticket.user_last_name}`;
    if (getSenderType(message, ticket) === 'seller') return `${ticket.seller_first_name || ''} ${ticket.seller_last_name || ''}`;
    
    return 'User';
  };

  const getStatusConfig = (status) => {
    const configs = {
      'open': { color: 'text-blue-400', bg: 'bg-blue-900/30', border: 'border-blue-800/50', icon: <FiAlertCircle /> },
      'in_progress': { color: 'text-yellow-400', bg: 'bg-yellow-900/30', border: 'border-yellow-800/50', icon: <FiActivity /> },
      'awaiting_user': { color: 'text-purple-400', bg: 'bg-purple-900/30', border: 'border-purple-800/50', icon: <FiUser /> },
      'awaiting_admin': { color: 'text-orange-400', bg: 'bg-orange-900/30', border: 'border-orange-800/50', icon: <FiClock /> },
      'resolved': { color: 'text-green-400', bg: 'bg-green-900/30', border: 'border-green-800/50', icon: <FiCheckCircle /> },
      'closed': { color: 'text-gray-400', bg: 'bg-gray-700', border: 'border-gray-600', icon: <FiShield /> }
    };
    return configs[status] || configs.closed;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      'urgent': { color: 'text-red-400', bg: 'bg-red-900/30', border: 'border-red-800/50' },
      'high': { color: 'text-orange-400', bg: 'bg-orange-900/30', border: 'border-orange-800/50' },
      'medium': { color: 'text-yellow-400', bg: 'bg-yellow-900/30', border: 'border-yellow-800/50' },
      'low': { color: 'text-blue-400', bg: 'bg-blue-900/30', border: 'border-blue-800/50' }
    };
    return configs[priority] || { color: 'text-gray-400', bg: 'bg-gray-700', border: 'border-gray-600' };
  };

  // Loading Skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="h-4 bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="space-y-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-3 bg-gray-700 rounded w-full"></div>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 h-96"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="text-center py-12">
          <div className="text-gray-600 text-6xl mb-4">📭</div>
          <h3 className="text-xl font-medium text-white mb-2">Ticket Not Found</h3>
          <p className="text-gray-400 mb-6">The ticket you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/support/tickets')}
            className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium"
          >
            Return to Tickets
          </button>
        </div>
      </div>
    );
  }

  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';
  const statusConfig = getStatusConfig(ticket.status);
  const priorityConfig = getPriorityConfig(ticket.priority);

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/support/tickets"
              className="text-gray-400 hover:text-white transition-colors flex items-center"
            >
              <FiArrowLeft className="mr-2" />
              <span className="hidden md:inline">Back to Tickets</span>
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                <span className="text-[#ff660e]">#{ticket.ticket_id}</span> - {ticket.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}>
                  {statusConfig.icon}
                  <span className="ml-1">{ticket.status.replace('_', ' ').toUpperCase()}</span>
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${priorityConfig.bg} ${priorityConfig.color} ${priorityConfig.border}`}>
                  {ticket.priority.toUpperCase()}
                </span>

                 {/* ADD WebSocket status indicator */}
                 {isConnected ? (
                  <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-900/30 text-green-400 border border-green-800/50">
                    <span className="w-2 h-2 rounded-full bg-green-400 mr-1 animate-pulse"></span>
                    Live
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-gray-700 text-gray-400 border border-gray-600">
                    <span className="w-2 h-2 rounded-full bg-gray-400 mr-1"></span>
                    Offline
                  </span>
                )}

                <span className="text-sm text-gray-400">
                  Created: {formatDate(ticket.created_at)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={fetchTicket}
              disabled={loading}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 font-medium flex items-center disabled:opacity-50"
            >
              <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 transition-all duration-200 font-medium flex items-center"
              >
                <FiMoreVertical className="mr-2" />
                Actions
                {showActions ? <FiChevronUp className="ml-2" /> : <FiChevronDown className="ml-2" />}
              </button>
              
              {showActions && (
                <div className="absolute right-0 mt-2 w-56 bg-gray-800 rounded-xl border border-gray-700 shadow-xl z-10 animate-fade-in">
                  <div className="py-1">
                    {isClosed ? (
                      <button
                        onClick={handleReopen}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 flex items-center"
                      >
                        <FiUnlock className="mr-2" />
                        Reopen Ticket
                      </button>
                    ) : (
                      <>
                        {!ticket.assigned_to && (
                          <button
                            onClick={handleAssignToMe}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 flex items-center"
                          >
                            <FiUserPlus className="mr-2" />
                            Assign to Me
                          </button>
                        )}
                        <button
                          onClick={() => window.print()}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 flex items-center"
                        >
                          <FiDownload className="mr-2" />
                          Export as PDF
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-900/20 flex items-center"
                        >
                          <FiTrash2 className="mr-2" />
                          Delete Ticket
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Ticket Info & Actions */}
        <div className="space-y-6">
          {/* User Information Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center mb-4">
              <FiUser className="text-[#ff660e] mr-2" />
              <h3 className="text-lg font-medium text-white">User Information</h3>
            </div>
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-full flex items-center justify-center">
                <span className="text-xl font-bold text-white">
                  {ticket.user_first_name?.[0]}{ticket.user_last_name?.[0]}
                </span>
              </div>
              <div>
                <h4 className="font-medium text-white">
                  {ticket.user_first_name} {ticket.user_last_name}
                </h4>
                <div className="flex items-center space-x-1 text-sm text-gray-400 mt-1">
                  <FiMail />
                  <span>{ticket.user_email}</span>
                </div>
                <button className="text-sm text-[#ff660e] hover:text-orange-400 mt-2 flex items-center">
                  <FiExternalLink className="mr-1" />
                  View Profile
                </button>
              </div>
            </div>
          </div>

          {/* Ticket Details Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center mb-4">
              <FiTag className="text-[#ff660e] mr-2" />
              <h3 className="text-lg font-medium text-white">Ticket Details</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Category</span>
                <span className="text-sm font-medium text-white">
                  {ticket.category_name || 'General'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Assigned To</span>
                <span className="text-sm font-medium text-white">
                  {ticket.assigned_first_name && ticket.assigned_last_name ? (
                    `${ticket.assigned_first_name} ${ticket.assigned_last_name}`
                  ) : (
                    <span className="text-gray-400">Unassigned</span>
                  )}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Last Updated</span>
                <span className="text-sm text-gray-300">
                  {formatDate(ticket.updated_at)}
                </span>
              </div>

              {ticket.resolved_at && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Resolved At</span>
                  <span className="text-sm text-green-400">
                    {formatDate(ticket.resolved_at)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center mb-4">
              <FiSettings className="text-[#ff660e] mr-2" />
              <h3 className="text-lg font-medium text-white">Quick Actions</h3>
            </div>
            <div className="space-y-4">
              {/* Status Change */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="awaiting_user">Awaiting User</option>
                  <option value="awaiting_admin">Awaiting Admin</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              
              {/* Priority Change */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                <select
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
                  value={ticket.priority}
                  onChange={(e) => handlePriorityChange(e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              
              {/* Assign Button */}
              {!ticket.assigned_to && (
                <button
                  onClick={handleAssignToMe}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center justify-center"
                >
                  <FiUserPlus className="mr-2" />
                  Assign to Me
                </button>
              )}
            </div>
          </div>

          {/* Related Items Card */}
          {(dispute || order) && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center mb-4">
                <FiGrid className="text-[#ff660e] mr-2" />
                <h3 className="text-lg font-medium text-white">Related Items</h3>
              </div>
              <div className="space-y-3">
                {dispute && (
                  <div className="p-3 bg-gradient-to-r from-yellow-900/20 to-amber-900/10 border border-yellow-700/30 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-yellow-400">Dispute</span>
                      <span className="text-xs text-yellow-600">#{dispute.dispute_id}</span>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">{dispute.status}</p>
                    <button
                      onClick={() => navigate(`/admin/disputes/${dispute.dispute_id}`)}
                      className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center"
                    >
                      <FiExternalLink className="mr-1" />
                      View Details
                    </button>
                  </div>
                )}
                
                {order && (
                  <div className="p-3 bg-gradient-to-r from-blue-900/20 to-cyan-900/10 border border-blue-700/30 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-blue-400">Order</span>
                      <span className="text-xs text-blue-600">#{order.order_id}</span>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">{order.status}</p>
                    <button
                      onClick={() => navigate(`/admin/orders/${order.order_id}`)}
                      className="text-xs text-blue-400 hover:text-blue-300 flex items-center"
                    >
                      <FiExternalLink className="mr-1" />
                      View Details
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Conversation */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tab Navigation */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="border-b border-gray-700">
              <nav className="flex -mb-px space-x-8 px-6">
                {[
                  { id: 'conversation', label: 'Conversation', icon: <FiMessageSquare />, count: messages.length },
                  { id: 'info', label: 'Ticket Info', icon: <FiTag /> },
                  ...(attachments.length > 0 ? [{ id: 'attachments', label: 'Attachments', icon: <FiPaperclip />, count: attachments.length }] : [])
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 flex items-center text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-[#ff660e] text-[#ff660e]'
                        : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    {tab.icon}
                    <span className="ml-2">{tab.label}</span>
                    {tab.count !== undefined && tab.count > 0 && (
                      <span className="ml-2 bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded-full">
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* Conversation Tab */}
              {activeTab === 'conversation' && (
                <div className="space-y-6">
                  {/* Ticket Description */}
                  {ticket.description && (
                    <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 mb-6">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-900/50 to-purple-900/50 flex items-center justify-center mr-3">
                          <span className="text-sm font-bold text-white">
                            {ticket.user_first_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-white">
                            {ticket.user_first_name} {ticket.user_last_name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {formatDate(ticket.created_at)} • Ticket Creator
                          </div>
                        </div>
                      </div>
                      <div className="prose max-w-none">
                        <p className="text-gray-300 whitespace-pre-line">{ticket.description}</p>
                      </div>
                    </div>
                  )}

                  {/* Messages List */}
                  <div className="space-y-4">
                    {allMessages.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-600 text-4xl mb-3">💬</div>
                        <p className="text-gray-400">No messages yet</p>
                        <p className="text-sm text-gray-500 mt-1">Start the conversation</p>
                      </div>
                    ) : (
                      allMessages.map((message, index) => {
                        // Use temp_id for temporary messages
                        const messageKey = message.message_id || message.temp_id || index;
                        
                        // Determine sender type
                        const senderType = getSenderType(message, ticket);
                        const senderName = getSenderName(message, ticket);
                        
                        return (
                          <div
                            key={messageKey}
                            className={`rounded-xl border p-4 ${
                              // For temporary messages (sending), add opacity
                              message.isTemp ? 'opacity-70 border-dashed animate-pulse' : ''
                            } ${
                              senderType === 'admin' || message.sender_type === 'admin' || message.is_internal
                                ? 'bg-gradient-to-r from-blue-900/20 to-cyan-900/10 border-blue-700/30' 
                                : senderType === 'system' || message.is_system
                                ? 'bg-gradient-to-r from-purple-900/20 to-indigo-900/10 border-purple-700/30'
                                : senderType === 'user' || message.sender_type === 'user'
                                ? 'bg-gradient-to-r from-gray-700/30 to-gray-600/10 border-gray-600'
                                : 'bg-gradient-to-r from-green-900/20 to-emerald-900/10 border-green-700/30'
                            }`}
                          >
                            {/* Loading indicator for temporary messages */}
                            {message.isTemp && (
                              <div className="flex items-center text-sm text-gray-400 mb-2">
                                <FiRefreshCw className="animate-spin mr-2" size={14} />
                                <span>Sending...</span>
                              </div>
                            )}
                            
                            {/* WebSocket real-time indicator */}
                            {!message.isTemp && message.message_id && message.created_at && 
                            Date.now() - new Date(message.created_at).getTime() < 30000 && (
                              <div className="flex items-center text-xs text-green-400 mb-1">
                                <span className="w-2 h-2 rounded-full bg-green-400 mr-2 animate-pulse"></span>
                                <span>Just now</span>
                              </div>
                            )}
                            
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  senderType === 'admin' || message.sender_type === 'admin' 
                                    ? 'bg-gradient-to-br from-blue-900 to-cyan-800' :
                                  senderType === 'system' || message.is_system 
                                    ? 'bg-gradient-to-br from-purple-800 to-indigo-700' :
                                  senderType === 'user' || message.sender_type === 'user'
                                    ? 'bg-gradient-to-br from-green-900 to-emerald-800' :
                                  'bg-gradient-to-br from-gray-800 to-gray-700'
                                }`}>
                                  <span className="text-sm font-bold text-white">
                                    {senderName?.[0] || (message.sender_type === 'admin' ? 'A' : 'U')}
                                  </span>
                                </div>
                                <div>
                                  <div className={`font-medium ${
                                    senderType === 'admin' || message.sender_type === 'admin' 
                                      ? 'text-blue-300' :
                                    senderType === 'system' || message.is_system
                                      ? 'text-purple-300' :
                                    senderType === 'user' || message.sender_type === 'user'
                                      ? 'text-green-300' :
                                    'text-gray-300'
                                  }`}>
                                    {senderName}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {formatDate(message.created_at)}
                                    {message.isTemp && ' • Sending...'}
                                  </div>
                                </div>
                              </div>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                senderType === 'admin' || message.sender_type === 'admin' 
                                  ? 'bg-blue-900/30 text-blue-400 border border-blue-800/50' :
                                senderType === 'system' || message.is_system
                                  ? 'bg-purple-900/30 text-purple-400 border border-purple-800/50' :
                                senderType === 'user' || message.sender_type === 'user'
                                  ? 'bg-green-900/30 text-green-400 border border-green-800/50' :
                                'bg-gray-700 text-gray-400 border border-gray-600'
                              }`}>
                                {senderType === 'admin' || message.sender_type === 'admin' ? 'Admin' :
                                senderType === 'system' || message.is_system ? 'System' :
                                senderType === 'seller' ? 'Seller' : 'User'}
                                
                                {/* Show if message is internal/admin-only */}
                                {message.is_internal && ' • Internal'}
                              </span>
                            </div>
                            
                            <div className="mt-2">
                              <p className="text-gray-300 whitespace-pre-line">
                                {message.message}
                              </p>
                              
                              {/* Show file attachments if any */}
                              {message.file_url && (
                                <div className="mt-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                                  <div className="flex items-center space-x-3">
                                    <FiPaperclip className="text-blue-400" />
                                    <div className="flex-1 min-w-0">
                                      <a 
                                        href={message.file_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-300 hover:text-blue-200 truncate block"
                                      >
                                        {message.file_name || 'Download file'}
                                      </a>
                                      {message.file_size && (
                                        <span className="text-xs text-gray-400">
                                          {(message.file_size / 1024).toFixed(1)} KB
                                        </span>
                                      )}
                                    </div>
                                    <FiExternalLink className="text-gray-500" size={14} />
                                  </div>
                                </div>
                              )}
                              
                              {/* Internal note indicator */}
                              {message.is_internal && (
                                <div className="mt-3 pt-3 border-t border-gray-600/50">
                                  <span className="inline-flex items-center px-2 py-1 text-xs bg-gray-700 text-gray-400 rounded">
                                    <FiLock className="mr-1" size={12} />
                                    Internal Note - User cannot see this
                                  </span>
                                </div>
                              )}
                              
                              {/* Message status indicator */}
                              {message.is_read && message.sender_type === 'admin' && (
                                <div className="mt-2 pt-2 border-t border-gray-600/30">
                                  <span className="inline-flex items-center text-xs text-gray-400">
                                    <FiCheck className="mr-1" size={12} />
                                    Read by user
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* WebSocket status badge for real-time messages */}
                            {!message.isTemp && Date.now() - new Date(message.created_at).getTime() < 5000 && (
                              <div className="absolute -top-2 -right-2">
                                <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-900/80 text-green-300 border border-green-700/50">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 mr-1 animate-pulse"></span>
                                  Live
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Reply Form - Only show if not closed */}
                  {!isClosed && (
                    <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 mt-6">
                      <h3 className="font-medium text-white mb-4">Add Response</h3>
                      <form onSubmit={handleSubmitReply}>
                        <div className="mb-4">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isInternal}
                              onChange={(e) => setIsInternal(e.target.checked)}
                              className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                            />
                            <span className="text-sm text-gray-300">Internal note (user won't see this)</span>
                          </label>
                        </div>
                        
                        <textarea
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500 h-32"
                          placeholder="Type your response here..."
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          disabled={submitting}
                        />
                        
                        <div className="flex justify-between items-center mt-4">
                          <div className="text-xs text-gray-400">
                            Press Enter to send
                          </div>
                          <button
                            type="submit"
                            disabled={!reply.trim() || submitting}
                            className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          >
                            {submitting ? (
                              <>
                                <FiRefreshCw className="animate-spin mr-2" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <FiMessageSquare className="mr-2" />
                                Send Response
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              )}

              {/* Info Tab */}
              {activeTab === 'info' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4">
                      <h4 className="text-sm font-medium text-gray-300 mb-3">Ticket Metadata</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">Ticket ID</span>
                          <span className="text-sm font-mono text-white">#{ticket.ticket_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">Created</span>
                          <span className="text-sm text-gray-300">{formatDate(ticket.created_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">Last Updated</span>
                          <span className="text-sm text-gray-300">{formatDate(ticket.updated_at)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-4">
                      <h4 className="text-sm font-medium text-gray-300 mb-3">Response Times</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">First Response</span>
                          <span className="text-sm text-green-400">2h 15m</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">Average Response</span>
                          <span className="text-sm text-blue-400">4h 30m</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">Resolution Time</span>
                          <span className="text-sm text-purple-400">12h 45m</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Attachments Tab */}
              {activeTab === 'attachments' && attachments.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-white mb-4">{attachments.length} Attachment{attachments.length === 1 ? '' : 's'}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {attachments.map((attachment, index) => (
                      <a
                        key={index}
                        href={attachment.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 hover:border-gray-500 transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-900/30 to-cyan-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <FiPaperclip className="text-blue-400 text-xl" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-white truncate">
                              {attachment.file_name || `Attachment ${index + 1}`}
                            </div>
                            <div className="text-sm text-gray-400">
                              {attachment.file_size ? `${(attachment.file_size / 1024).toFixed(1)}KB` : 'Unknown size'}
                            </div>
                          </div>
                          <FiDownload className="text-gray-500 group-hover:text-white transition-colors" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md animate-fade-in">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FiAlertCircle className="text-red-400 mr-2" />
                  <h3 className="text-lg font-medium text-white">Delete Ticket</h3>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteReason('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-gray-300">
                This action cannot be undone. The ticket and all related messages will be permanently deleted.
              </p>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Reason for deletion
                </label>
                <textarea
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-white placeholder-gray-500 h-24"
                  placeholder="Please provide a reason for deleting this ticket..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                />
              </div>
            </div>

            <div className="p-6 pt-0 border-t border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteReason('');
                }}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!deleteReason.trim()}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .prose {
          color: #d1d5db;
        }
        .prose p {
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
      `}</style>
    </div>
  );
};

export default TicketDetail;